import ExifReader from "exifreader";
import * as exifr from "exifr";
import Psd from "@webtoon/psd";
import type { AssetMetadata, MediaKind } from "@focalfolio/domain-model";

interface LooseTag {
  value?: unknown;
  description?: string | number;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function tagValue(tags: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    const candidate = tags[key] as LooseTag | unknown;
    if (candidate && typeof candidate === "object") {
      const tag = candidate as LooseTag;
      if (tag.description !== undefined) return tag.description;
      if (tag.value !== undefined) return tag.value;
    }
    if (candidate !== undefined) return candidate;
  }
  return undefined;
}

function stringValue(value: unknown): string | undefined {
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (value === null || value === undefined || value === "") return undefined;
  return String(value);
}

function numberValue(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number.parseFloat(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : undefined;
}

function keywordsValue(value: unknown): string[] {
  const values = Array.isArray(value) ? value : typeof value === "string" ? value.split(/[;,]/u) : [];
  return [...new Set(values.map(String).map((entry) => entry.trim()).filter(Boolean))];
}

function normalizeExifDate(value: unknown): string | undefined {
  const text = stringValue(value);
  if (!text) return undefined;
  const normalized = text.replace(/^(\d{4}):(\d{2}):(\d{2})/u, "$1-$2-$3");
  const date = new Date(normalized);
  return Number.isNaN(date.valueOf()) ? text : date.toISOString();
}

export interface MetadataReadResult {
  metadata: AssetMetadata;
  warnings: string[];
  rawTags: Record<string, unknown>;
}

export async function readMetadata(file: File): Promise<MetadataReadResult> {
  const warnings: string[] = [];
  let loaded: unknown = {};
  try {
    loaded = await ExifReader.load(file, { expanded: true, includeUnknown: false });
  } catch (error) {
    warnings.push(`ExifReader: ${error instanceof Error ? error.message : String(error)}`);
  }

  const expanded = asRecord(loaded);
  const flat = {
    ...asRecord(expanded.file),
    ...asRecord(expanded.exif),
    ...asRecord(expanded.iptc),
    ...asRecord(expanded.xmp),
    ...asRecord(expanded.icc),
    ...expanded
  };

  const metadata: AssetMetadata = {
    keywords: keywordsValue(tagValue(flat, "Keywords", "Subject", "dc:subject")),
    title: stringValue(tagValue(flat, "Title", "ObjectName", "dc:title")),
    description: stringValue(tagValue(flat, "Description", "ImageDescription", "Caption/Abstract", "dc:description")),
    creator: stringValue(tagValue(flat, "Creator", "Artist", "By-line", "dc:creator")),
    copyright: stringValue(tagValue(flat, "Copyright", "CopyrightNotice", "dc:rights")),
    usageTerms: stringValue(tagValue(flat, "UsageTerms", "xmpRights:UsageTerms")),
    cameraMake: stringValue(tagValue(flat, "Make")),
    cameraModel: stringValue(tagValue(flat, "Model")),
    lens: stringValue(tagValue(flat, "LensModel", "Lens")),
    captureDate: normalizeExifDate(tagValue(flat, "DateTimeOriginal", "CreateDate", "DateCreated")),
    width: numberValue(tagValue(flat, "Image Width", "ImageWidth", "PixelXDimension")),
    height: numberValue(tagValue(flat, "Image Height", "ImageHeight", "PixelYDimension")),
    iso: numberValue(tagValue(flat, "ISOSpeedRatings", "PhotographicSensitivity", "ISO")),
    aperture: numberValue(tagValue(flat, "FNumber", "ApertureValue")),
    shutterSpeed: stringValue(tagValue(flat, "ExposureTime", "ShutterSpeedValue")),
    focalLength: numberValue(tagValue(flat, "FocalLength")),
    orientation: numberValue(tagValue(flat, "Orientation")),
    raw: Object.fromEntries(
      Object.entries(flat)
        .slice(0, 300)
        .map(([key, value]) => [key, value && typeof value === "object" ? (value as LooseTag).description ?? (value as LooseTag).value : value])
    )
  };
  return { metadata, warnings, rawTags: flat };
}

export async function extractEmbeddedThumbnail(file: File): Promise<Blob | undefined> {
  try {
    const bytes = await exifr.thumbnail(file);
    if (bytes && bytes.byteLength > 0) return new Blob([new Uint8Array(bytes)], { type: "image/jpeg" });
  } catch {
    // Not all formats contain an EXIF thumbnail.
  }
  return undefined;
}

function findLargestJpeg(bytes: Uint8Array): Uint8Array | undefined {
  let best: Uint8Array | undefined;
  let start = -1;
  for (let index = 0; index < bytes.length - 1; index += 1) {
    if (bytes[index] === 0xff && bytes[index + 1] === 0xd8) {
      start = index;
      index += 1;
      continue;
    }
    if (start >= 0 && bytes[index] === 0xff && bytes[index + 1] === 0xd9) {
      const candidate = bytes.slice(start, index + 2);
      if (!best || candidate.length > best.length) best = candidate;
      start = -1;
      index += 1;
    }
  }
  return best;
}

export async function extractDocumentEmbeddedPreview(file: File): Promise<Blob | undefined> {
  const maximum = Math.min(file.size, 64 * 1024 * 1024);
  const bytes = new Uint8Array(await file.slice(0, maximum).arrayBuffer());
  const jpeg = findLargestJpeg(bytes);
  if (!jpeg || jpeg.byteLength <= 1024) return undefined;
  const copy = jpeg.buffer.slice(jpeg.byteOffset, jpeg.byteOffset + jpeg.byteLength) as ArrayBuffer;
  return new Blob([copy], { type: "image/jpeg" });
}

export async function renderPsdComposite(file: File, maximumDimension = 4096): Promise<Blob> {
  const buffer = await file.arrayBuffer();
  const psd = Psd.parse(buffer);
  const pixels = await psd.composite(true, true);
  const scale = Math.min(1, maximumDimension / Math.max(psd.width, psd.height));
  const source = new OffscreenCanvas(psd.width, psd.height);
  const sourceContext = source.getContext("2d");
  if (!sourceContext) throw new Error("Canvas 2D rendering is unavailable.");
  const pixelCopy = new Uint8ClampedArray(pixels.length);
  pixelCopy.set(pixels);
  sourceContext.putImageData(new ImageData(pixelCopy, psd.width, psd.height), 0, 0);
  const target = new OffscreenCanvas(Math.max(1, Math.round(psd.width * scale)), Math.max(1, Math.round(psd.height * scale)));
  const targetContext = target.getContext("2d");
  if (!targetContext) throw new Error("Canvas 2D rendering is unavailable.");
  targetContext.drawImage(source, 0, 0, target.width, target.height);
  return target.convertToBlob({ type: "image/png" });
}

export async function createLocalPreviewBlob(file: File, kind: MediaKind): Promise<Blob | undefined> {
  if (kind === "image") return file;
  if (kind === "raw") return extractEmbeddedThumbnail(file);
  if (kind === "photoshop") {
    try {
      return await renderPsdComposite(file);
    } catch {
      return extractDocumentEmbeddedPreview(file);
    }
  }
  if (kind === "illustrator" || kind === "indesign") return extractDocumentEmbeddedPreview(file);
  return undefined;
}

export async function imageDimensions(blob: Blob): Promise<{ width: number; height: number }> {
  const bitmap = await createImageBitmap(blob);
  try {
    return { width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
}
